/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {
  ActionButton,
  DialogContainer,
  DialogTrigger
} from '@adobe/react-spectrum';
import {injectIntl} from 'react-intl';
import * as intlMessages from './__localization__/ShellHeaderUserProfile';
import {IntlProvider} from '@quarry/intl';
import React, {useRef, useState} from 'react';
import ShellAvatar from '../ShellAvatar';
import ShellLegalNoticesDialog from './ShellLegalNoticesDialog';
import UserProfilePopover from './Popover';
import '../ShellHeader.scss';
import '../ShellHeaderActions.scss';

const ShellHeaderUserProfile = ({intl, props, tabIndex}) => {
  const {
    currentActiveTenantId,
    customProfileButtons,
    closeOverlaysOnMenuShow,
    environment,
    hasAccountClusterData,
    hasSandboxBar,
    imsOrgs,
    internal,
    locale,
    noAccess,
    onCustomProfileButtonClick,
    onImsOrgChange,
    onSetUserTheme,
    onSignOutClick,
    orgToSubOrgsMap,
    selectedImsOrg,
    selectedSubOrg,
    showDebugModal,
    subOrgAccessOnlyOnCurrentOrg,
    theme,
    userAvatar,
    userEmail,
    userFirstName,
    userLastName,
    userName,
    userTitle
  } = props;
  const [legalDialogActive, setLegalDialogActive] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const userProfileRef = useRef(null);
  let closeMenu;

  return (
    <>
      <DialogTrigger
        hideArrow
        isOpen={isPopoverOpen}
        key="profile-menu"
        onOpenChange={isOpen => {
          // This can be removed once all of shell header is using V3
          isOpen && closeOverlaysOnMenuShow(userProfileRef);
          setTimeout(() => setIsPopoverOpen(isOpen), 0);
        }}
        placement="bottom"
        ref={userProfileRef}
        type="popover"
      >
        <ActionButton
          UNSAFE_className="shell-avatar"
          aria-expanded={isPopoverOpen}
          aria-label={intl.formatMessage({
            defaultMessage: 'Profile',
            description: 'Profile Button Label',
            id: 'profile'})}
          data-omega-element="user profile"
          isDisabled={tabIndex === -1}
          isQuiet
          key="profile-menu-button"
          marginStart="size-100"
        >
          <ShellAvatar src={userAvatar} />
        </ActionButton>
        {close => {
          closeMenu = close;
          return (
            <UserProfilePopover
              avatarUrl={userAvatar}
              closeMenu={close}
              currentActiveTenantId={currentActiveTenantId}
              customProfileButtons={customProfileButtons}
              email={userEmail}
              environment={environment}
              firstName={userFirstName}
              hasAccountClusterData={hasAccountClusterData}
              hasSandboxBar={hasSandboxBar}
              imsOrgs={imsOrgs}
              internal={internal}
              lastName={userLastName}
              locale={locale}
              name={userName}
              noAccess={noAccess}
              onCustomProfileButtonClick={onCustomProfileButtonClick}
              onImsOrgChange={onImsOrgChange}
              onSetUserTheme={onSetUserTheme}
              onSignOutClick={onSignOutClick}
              orgToSubOrgsMap={orgToSubOrgsMap}
              selectedImsOrg={selectedImsOrg}
              selectedSubOrg={selectedSubOrg}
              setLegalDialogActive={setLegalDialogActive}
              showDebugModal={showDebugModal}
              subOrgAccessOnlyOnCurrentOrg={subOrgAccessOnlyOnCurrentOrg}
              subheading={userTitle}
              theme={theme}
            />
          );
        }}
      </DialogTrigger>
      <DialogContainer onDismiss={() => {
        setLegalDialogActive(false);
        closeMenu();
      }}>
        {legalDialogActive && <ShellLegalNoticesDialog />}
      </DialogContainer>
    </>
  );
};

const withI18nProvider = Component => function LocalizedComponent(props) {
  return (
    <IntlProvider locale={props.locale} messages={intlMessages}>
      <Component
        {...props}
      />
    </IntlProvider>
  );
};

export default withI18nProvider(injectIntl(ShellHeaderUserProfile));
